"use client"

import { useState, useRef, useCallback } from "react"

export const useAudioPlayback = () => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioUrl, setAudioUrl] = useState(null)
  const [playbackTime, setPlaybackTime] = useState("00:00")
  const [audioDuration, setAudioDuration] = useState("00:00")
  const [remainingTime, setRemainingTime] = useState("00:00")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [volume, setVolume] = useState(1.0)

  const audioRef = useRef(null)
  const playbackTimerRef = useRef(null)

  const startPlaybackTimer = useCallback(() => {
    playbackTimerRef.current = setInterval(() => {
      if (audioRef.current) {
        const currentTime = audioRef.current.currentTime
        const duration = audioRef.current.duration
        
        // Update current playback time
        const minutes = String(Math.floor(currentTime / 60)).padStart(2, "0")
        const seconds = String(Math.floor(currentTime % 60)).padStart(2, "0")
        setPlaybackTime(`${minutes}:${seconds}`)
        
        // Update remaining time
        if (duration && !isNaN(duration)) {
          const remaining = duration - currentTime
          const remainingMinutes = String(Math.floor(remaining / 60)).padStart(2, "0")
          const remainingSeconds = String(Math.floor(remaining % 60)).padStart(2, "0")
          setRemainingTime(`${remainingMinutes}:${remainingSeconds}`)
        }
      }
    }, 1000)
  }, [])

  const stopPlaybackTimer = useCallback(() => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current)
    }
  }, [])

  const playAudio = useCallback(async () => {
    if (audioRef.current) {
      try {
        setIsLoading(true)
        setError(null)
        
        audioRef.current.volume = volume
        
        await audioRef.current.play()
        setIsPlaying(true)
        setIsLoading(false)
        startPlaybackTimer()
      } catch (error) {
        console.error('🎵 Audio playback error:', error)
        setError(error.message)
        setIsPlaying(false)
        setIsLoading(false)
      }
    }
  }, [startPlaybackTimer, volume])

  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
      stopPlaybackTimer()
    }
  }, [stopPlaybackTimer])

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
      setPlaybackTime("00:00")
      setRemainingTime("00:00")
      stopPlaybackTimer()
    }
  }, [stopPlaybackTimer])

  const handleAudioLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      const duration = audioRef.current.duration;      
      if (duration && !isNaN(duration)) {
        const minutes = String(Math.floor(duration / 60)).padStart(2, "0")
        const seconds = String(Math.floor(duration % 60)).padStart(2, "0")
        setAudioDuration(`${minutes}:${seconds}`)
        setRemainingTime(`${minutes}:${seconds}`)
      }
    }
  }, [])

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false)
    setPlaybackTime("00:00")
    setRemainingTime("00:00")
    stopPlaybackTimer()
  }, [stopPlaybackTimer])

  const handleAudioError = useCallback((error) => {
    setError('Failed to load audio file')
    setIsPlaying(false)
    setIsLoading(false)
  }, [])

  const handleAudioCanPlay = useCallback(() => {
    setIsLoading(false)
    setError(null)
  }, [])

  const clearAudio = useCallback(() => {
    setAudioUrl(null)
    setPlaybackTime("00:00")
    setAudioDuration("00:00")
    setRemainingTime("00:00")
    setIsPlaying(false)
    setIsLoading(false)
    setError(null)
  }, [])

  const setAudioVolume = useCallback((newVolume) => {
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume
    }
  }, [])

  const seekTo = useCallback((timeInSeconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = timeInSeconds
    }
  }, [])

  return {
    isPlaying,
    audioUrl,
    playbackTime,
    audioDuration,
    remainingTime,
    isLoading,
    error,
    volume,
    audioRef,
    setAudioUrl,
    playAudio,
    pauseAudio,
    stopAudio,
    handleAudioLoadedMetadata,
    handleAudioEnded,
    handleAudioError,
    handleAudioCanPlay,
    clearAudio,
    setAudioVolume,
    seekTo,
  }
}
